'use client'

import { useCallback } from 'react'
import {
  QueryKey,
  UseMutationOptions,
  UseQueryOptions,
  useMutation,
  useQuery,
  useQueryClient
} from '@tanstack/react-query'

import { useClientApi } from '../useClientApi'

import type {
  TGetShareOutput,
  TShareVesselJourneyInput,
  TShareVesselJourneyOutput
} from '@/api/v1/resources/vesselJourney'

export type TOptionsQueryShareVessel = UseQueryOptions<
  TGetShareOutput,
  unknown,
  TGetShareOutput,
  QueryKey
>

export const getShareVesselKey = (code?: string): QueryKey => [
  'v1',
  'vesselJourney',
  'share',
  { code: code?.trim() }
]

export const useQueryShareVessel = (
  code: string | undefined,
  options?: TOptionsQueryShareVessel
) => {
  const trimmed = code?.trim()
  const { api } = useClientApi()
  return useQuery({
    queryKey: getShareVesselKey(trimmed),
    queryFn: () => {
      if (!trimmed) throw new Error('Invalid share code')
      return api.vesselJourney.getShare({ code: trimmed })
    },
    enabled: !!trimmed,
    ...options
  })
}

export const useMutationShareVessel = (
  options?: UseMutationOptions<TShareVesselJourneyOutput, unknown, TShareVesselJourneyInput>
) => {
  const { api } = useClientApi()
  return useMutation({
    mutationFn: async (input: TShareVesselJourneyInput) => {
      return api.vesselJourney.share(input)
    },
    ...options
  })
}

/**
 * useShareVessel
 *
 * Provides:
 * - Query to retrieve shared vessel data (GET /v1/vesselJourney/share/:code)
 * - Mutation to create a share (POST /v1/vesselJourney/share)
 */
export default function useShareVessel(code?: string, options?: TOptionsQueryShareVessel) {
  const queryClient = useQueryClient()

  const queryHook = useQueryShareVessel(code, options)

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: getShareVesselKey(code) })
  }, [queryClient, code])

  const post = useMutationShareVessel({
    onSuccess: () => invalidate()
  })

  return {
    // query
    ...queryHook,
    invalidate,

    // mutations
    post
  }
}
